import { loadStripe } from '@stripe/stripe-js';
import { supabase } from './supabase';

// Production Stripe Price IDs - Replace with your actual Stripe price IDs
const STRIPE_PRODUCTS = {
  starter: 'price_1QVoiceVisionStarterMonthly',
  pro: 'price_1QVoiceVisionProMonthly',
  credits_100: 'price_1QVoiceVisionCredits100',
  credits_500: 'price_1QVoiceVisionCredits500',
} as const;

// Initialize Stripe with environment-specific key
const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '',
  { apiVersion: '2023-10-16' }
);

export const createCheckoutSession = async (planType: 'starter' | 'pro') => {
  try {
    console.log('Creating checkout session for plan:', planType);
    console.log('Environment check:', {
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL?.substring(0, 20) + '...',
      hasStripeKey: !!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
      mode: import.meta.env.VITE_MODE || 'development'
    });
    
    // Get current user session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      throw new Error('Please sign in to upgrade your plan');
    }
    
    console.log('Calling Supabase function with user:', session.user.id);
    
    const { data, error } = await supabase.functions.invoke('stripe/create-checkout', {
      body: { 
        planType,
        priceId: STRIPE_PRODUCTS[planType],
      },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) {
      console.error('Supabase function error:', error);
      throw new Error(`Checkout failed: ${error.message || 'Please try again or contact support'}`);
    }

    console.log('Checkout session response:', data);

    const stripe = await stripePromise;
    if (!stripe) {
      console.error('Stripe failed to load');
      throw new Error('Stripe failed to load');
    }

    console.log('Stripe: Redirecting to checkout with session ID:', data.sessionId);
    const { error: stripeError } = await stripe.redirectToCheckout({ 
      sessionId: data.sessionId 
    });
    
    if (stripeError) {
      console.error('Stripe redirect error:', stripeError);
      throw new Error(`Payment redirect failed: ${stripeError.message}`);
    }
  } catch (error) {
    console.error('Checkout error:', error);
    alert(`Checkout failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    throw error;
  }
};

export const purchaseCredits = async (amount: '100' | '500') => {
  try {
    console.log('Purchasing credits:', amount);
    console.log('Stripe: Credits purchase initiated');
    
    const priceId = amount === '100' ? STRIPE_PRODUCTS.credits_100 : STRIPE_PRODUCTS.credits_500;
    console.log('Stripe: Using price ID:', priceId);

    const { data, error } = await supabase.functions.invoke('stripe/create-checkout', {
      body: { 
        type: 'credits',
        priceId,
        amount: parseInt(amount),
      },
    });

    if (error) {
      console.error('Credits purchase error:', error);
      console.error('Stripe: Credits error details:', JSON.stringify(error, null, 2));
      throw error;
    }

    const stripe = await stripePromise;
    if (!stripe) {
      console.error('Stripe failed to load for credits');
      console.error('Stripe: Credits - stripe instance failed to initialize');
      throw new Error('Stripe failed to load');
    }

    console.log('Stripe: Credits checkout session created:', data.sessionId);
    const { error: stripeError } = await stripe.redirectToCheckout({ 
      sessionId: data.sessionId 
    });
    
    if (stripeError) {
      console.error('Credits checkout error:', stripeError);
      console.error('Stripe: Credits redirect error details:', JSON.stringify(stripeError, null, 2));
      throw stripeError;
    }
  } catch (error) {
    console.error('Credit purchase error:', error);
    console.error('Stripe: Full credits error:', error);
    throw error;
  }
};

export const createCustomerPortalSession = async () => {
  try {
    console.log('Creating customer portal session...');
    console.log('Stripe: Portal session request initiated');
    
    const { data, error } = await supabase.functions.invoke('stripe/customer-portal', {
      body: {},
    });

    if (error) {
      console.error('Customer portal error:', error);
      console.error('Stripe: Portal error details:', JSON.stringify(error, null, 2));
      throw error;
    }

    console.log('Customer portal URL:', data.url);
    console.log('Stripe: Portal session created successfully');
    
    // Redirect to customer portal
    window.location.href = data.url;
  } catch (error) {
    console.error('Customer portal error:', error);
    console.error('Stripe: Full portal error:', error);
    throw error;
  }
};